import React from "react";
import { Dialog, Box, Typography, IconButton, Button } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import designTokens from "../../styles/designTokens";

interface CapacityExceededModalProps {
  open: boolean;
  onClose: () => void;
  onCancelUpload: () => void;
  onContactSupport: () => void;
  fileName?: string | null;
  convertedSizeMB?: number | null;
}

const formatSizeText = (value?: number | null): string | null => {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) {
    return null;
  }

  if (value >= 1024) {
    return `${(value / 1024).toFixed(1)} GB`;
  }

  return `${value.toFixed(1)} MB`;
};

const CapacityExceededModal: React.FC<CapacityExceededModalProps> = ({
  open,
  onClose,
  onCancelUpload,
  onContactSupport,
  fileName,
  convertedSizeMB,
}) => {
  const convertedSizeText = formatSizeText(convertedSizeMB);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      scroll="body"
      maxWidth={false}
      slotProps={{
        backdrop: { sx: { backgroundColor: "rgba(0,0,0,0.5)" } },
      }}
      PaperProps={{
        sx: {
          width: 500,
          minWidth: 500,
          backgroundColor: designTokens.fill.background.grey20.dark,
          color: designTokens.fill.text.common100.dark,
          borderRadius: designTokens.numbers.cornerRadius[16],
          overflow: "hidden",
          boxShadow: designTokens.shadows.modal,
        },
      }}
    >
      <Box sx={{ height: 16, width: "100%" }} />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: 64,
          px: designTokens.numbers.gap[32],
          gap: designTokens.numbers.gap[8],
        }}
      >
        <Typography
          sx={{
            ...designTokens.typography.styles.heading,
            color: designTokens.fill.text.grey90.dark,
            flex: 1,
          }}
        >
          File exceeds supported capacity.
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ color: designTokens.fill.icon.grey80.dark, p: 0, width: 24, height: 24 }}
        >
          <CloseIcon sx={{ fontSize: 24 }} />
        </IconButton>
      </Box>

      <Box sx={{ px: designTokens.numbers.gap[32] }}>
        <Typography
          sx={{
            ...designTokens.typography.styles.headline1,
            color: designTokens.fill.text.grey90.dark,
            mb: designTokens.numbers.gap[20],
            whiteSpace: "pre-line",
          }}
        >
          {"After conversion, this file exceeds the capacity limit.\nPlease contact View R support team for assistance."}
        </Typography>

        {fileName ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: "17px",
              py: "13px",
              mb: designTokens.numbers.gap[16],
              borderRadius: designTokens.numbers.cornerRadius[8],
              backgroundColor: "rgba(255,66,66,0.05)",
              border: "1px solid rgba(255,66,66,0.2)",
            }}
          >
            <Typography
              sx={{
                fontFamily: designTokens.typography.fontFamily.roboto,
                fontSize: 14,
                lineHeight: "21px",
                color: designTokens.fill.text.common100.dark,
              }}
            >
              {fileName}
            </Typography>

            {convertedSizeText ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: designTokens.numbers.gap[4] }}>
                <Typography
                  sx={{
                    fontFamily: designTokens.typography.fontFamily.roboto,
                    fontSize: 14,
                    lineHeight: "21px",
                    color: "#ff4242",
                  }}
                >
                  {convertedSizeText}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: designTokens.typography.fontFamily.roboto,
                    fontSize: 12,
                    lineHeight: "14px",
                    color: designTokens.fill.text.grey60.dark,
                  }}
                >
                  (converted)
                </Typography>
              </Box>
            ) : null}
          </Box>
        ) : null}

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: designTokens.numbers.gap[24],
            height: 64,
            alignItems: "center",
          }}
        >
          <Button
            variant="outlined"
            onClick={onCancelUpload}
            sx={{
              height: 48,
              minWidth: 100,
              px: designTokens.numbers.gap[12],
              textTransform: "none",
              borderColor: designTokens.stroke.grey60.dark,
              color: designTokens.fill.text.grey80.dark,
              borderRadius: designTokens.numbers.cornerRadius[8],
            }}
          >
            Upload cancel
          </Button>
          <Button
            variant="contained"
            onClick={onContactSupport}
            sx={{
              height: 48,
              minWidth: 100,
              px: designTokens.numbers.gap[12],
              textTransform: "none",
              backgroundColor: designTokens.fill.shape.primary50.dark,
              color: designTokens.fill.text.common100.dark,
              borderRadius: designTokens.numbers.cornerRadius[8],
              "&:hover": { backgroundColor: designTokens.fill.shape.primary50.dark },
            }}
          >
            Contact support
          </Button>
        </Box>
      </Box>
      <Box sx={{ height: 16, width: "100%" }} />
    </Dialog>
  );
};

export default CapacityExceededModal;
